/**
 * Main Layout Component
 * 
 * Application shell with sidebar navigation.
 * 
 * @validates Requirements 16.1, 16.2, 16.3 - Responsive layout
 */

import { useState, useMemo } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { useAccountStore } from '../../store/accountStore';
import { cn } from '../../utils/cn';
import { UserRole } from '@budgira/shared';
import Footer from './Footer';
import { LanguageSelector } from '../common/LanguageSelector';
import { NotificationBell } from '../common/NotificationBell';

const MainLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, logout, isAdmin, canManageSchedulers } = useAuthStore();
  const { accounts } = useAccountStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Check if user can use reconciliation
  // - Must be Premium or Admin (or secondary user with premium effective role)
  // - Must have at least one account with write access
  const canUseReconciliation = useMemo(() => {
    if (!user) {
      return false;
    }
    
    // For secondary users (shared access), check effective role and account permissions FIRST
    // (their own user.role may be 'free' but they inherit effectiveRole from owner)
    if (user.sharedAccess) {
      // Must have premium or admin effective role
      const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
      
      if (!hasPremiumRole) {
        return false;
      }
      
      // If accounts not loaded yet, allow access (will be blocked by API if no write access)
      if (accounts.length === 0) {
        return true;
      }
      
      // Check if at least one account has write or owner permission
      const hasWriteAccess = accounts.some(
        (account) => account.permission === 'write' || account.permission === 'owner'
      );
      return hasWriteAccess;
    }
    
    // For primary users (no sharedAccess), check their own role
    // Free users cannot use reconciliation
    if (user.role === UserRole.FREE) {
      return false;
    }
    
    // Premium and admin primary users can always use it
    return true;
  }, [user, accounts]);

  // Build navigation based on permissions
  const baseNavigation = [
    { name: t('nav.dashboard'), href: '/dashboard', icon: HomeIcon, show: true },
    { name: t('nav.statistics'), href: '/statistics', icon: ChartIcon, show: true },
    { name: t('nav.accounts'), href: '/accounts', icon: BankIcon, show: true },
    { name: t('nav.operations'), href: '/operations', icon: ListIcon, show: true },
    { name: t('nav.schedulers'), href: '/schedulers', icon: CalendarIcon, show: canManageSchedulers() },
    { name: t('nav.checkbooks'), href: '/checkbooks', icon: CheckbookIcon, show: true },
    { name: t('nav.projects'), href: '/projects', icon: FolderIcon, show: true },
    { name: t('nav.reconciliation'), href: '/reconciliation', icon: ReconciliationIcon, show: canUseReconciliation },
    { name: t('nav.bilans'), href: '/bilans', icon: BilanIcon, show: true },
    { name: t('nav.settings'), href: '/settings', icon: UserSettingsIcon, show: true },
  ];
  
  const navigation = baseNavigation.filter(item => item.show);

  // Check if current path matches a navigation item
  const isNavActive = (href: string) => {
    const path = location.pathname;
    
    // Special case: schedulers can be under /accounts/:id/schedulers
    if (href === '/schedulers') {
      return path === '/schedulers' || path.includes('/schedulers');
    }
    
    // Special case: operations can be under /accounts/:id/operations
    if (href === '/operations') {
      return path === '/operations' || (path.includes('/operations') && !path.includes('/schedulers'));
    }
    
    // Special case: accounts - only match /accounts exactly or /accounts/new or /accounts/:id/edit
    if (href === '/accounts') {
      return path === '/accounts' || path === '/accounts/new' || path.endsWith('/edit');
    }
    
    // Default: exact match or starts with href/
    return path === href || path.startsWith(href + '/');
  };

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-900 overflow-hidden">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 transform transition-transform lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center h-16 px-6 border-b border-slate-200 dark:border-slate-700">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/budgira.svg" alt="Budgira" className="w-8 h-8" />
              <span className="text-2xl font-display font-bold text-violet-600">Budgira</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = isNavActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    'flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px]',
                    isActive
                      ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-200'
                  )}
                >
                  <item.icon className={cn('w-5 h-5 mr-3', isActive ? 'text-violet-600 dark:text-violet-400' : 'text-slate-400 dark:text-slate-500')} />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{user?.email}</p>
            <button
              onClick={() => logout()}
              className="mt-3 w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors min-h-[44px]"
            >
              {t('auth.logout')}
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* Top bar */}
        <header className="shrink-0 z-30 flex items-center h-16 px-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 lg:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 text-slate-600 dark:text-slate-400 lg:hidden min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <MenuIcon className="w-6 h-6" />
          </button>
          
          {/* Account type badge - clickable for admin on mobile */}
          {isAdmin() ? (
            <Link
              to="/admin/templates"
              className="flex items-center gap-2 ml-2 lg:ml-0 px-3 py-1.5 bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded-lg text-sm hover:bg-violet-100 dark:hover:bg-violet-900/50 transition-colors"
            >
              <UserBadgeIcon className="w-4 h-4 flex-shrink-0" />
              <span>{t(`roles.${user?.role || 'free'}`)}</span>
            </Link>
          ) : (
            <div className="flex items-center gap-2 ml-2 lg:ml-0 px-3 py-1.5 bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded-lg text-sm">
              {user?.sharedAccess?.ownerEmail ? (
                <>
                  <LinkIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{t('sharedAccess.linkedTo', { email: user.sharedAccess.ownerEmail })}</span>
                  <span className="sm:hidden">{t('sharedAccess.linked', 'Lié')}</span>
                </>
              ) : (
                <>
                  <UserBadgeIcon className="w-4 h-4 flex-shrink-0" />
                  <span>{t(`roles.${user?.role || 'free'}`)}</span>
                </>
              )}
            </div>
          )}
          
          <div className="flex-1" />
          
          {/* Premium upgrade button for FREE users */}
          {user?.role === UserRole.FREE && !user?.sharedAccess?.ownerEmail && (
            <Link
              to="/premium"
              className="flex items-center gap-2 px-3 py-2 mr-2 text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50 rounded-lg transition-colors min-h-[44px]"
            >
              <CrownIcon className="w-5 h-5 text-amber-500 dark:text-amber-400" />
              <span className="hidden sm:inline">{t('nav.upgradeToPremium')}</span>
            </Link>
          )}
          
          {/* Notification bell */}
          <div className="mr-1">
            <NotificationBell />
          </div>
          
          {/* Language selector */}
          <LanguageSelector variant="compact" />
          
          {/* Admin button - only on desktop, mobile uses clickable badge */}
          {/* Removed: admin access is now via the clickable badge for all screen sizes */}
        </header>

        {/* Page content */}
        <main className="flex-1 flex flex-col min-h-0 p-3 sm:p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

// Simple icon components
const HomeIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const ChartIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
  </svg>
);

const BankIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const ListIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

const CalendarIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const FolderIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const CheckbookIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ReconciliationIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

const BilanIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const UserSettingsIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const MenuIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const LinkIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

const UserBadgeIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CrownIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z" />
  </svg>
);

export default MainLayout;
